import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, Avatar, IconButton,
    Tooltip, InputAdornment, Divider
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PreviewIcon from '@mui/icons-material/Preview';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InfoIcon from '@mui/icons-material/Info';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const SquadreCreatePage = () => {
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati del form
    const [formData, setFormData] = React.useState({
        teamNome: '',
        legaId: '',
        sportId: '',
        foto: '',
        ordinamento: '',
        note: '',
        flVisibile: true,
        flAttivo: true
    });

    // Stati di controllo
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});

    // Dati per dropdown
    const [leghe, setLeghe] = React.useState([]);
    const [sports, setSports] = React.useState([]);
    const [loadingLeghe, setLoadingLeghe] = React.useState(true);
    const [loadingSports, setLoadingSports] = React.useState(true);

    // Preview foto
    const [fotoPreview, setFotoPreview] = React.useState('');
    const [fotoError, setFotoError] = React.useState(false);

    // Carica leghe
    const loadLeghe = async () => {
        try {
            setLoadingLeghe(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-lega`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    flVisibile: 1,
                    flAttivo: 1,
                    $sort: { nome: 1 }
                }
            });
            setLeghe(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento leghe:", error);
            notify("Errore nel caricamento leghe", { type: 'error' });
        } finally {
            setLoadingLeghe(false);
        }
    };

    // Carica sport
    const loadSports = async () => {
        try {
            setLoadingSports(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-sport`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $sort: { nome: 1 }
                }
            });
            setSports(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento sport:", error);
            notify("Errore nel caricamento sport", { type: 'error' });
        } finally {
            setLoadingSports(false);
        }
    };

    // Validazione campi
    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'teamNome':
                if (!value.trim()) {
                    newErrors.teamNome = 'Il nome della squadra è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.teamNome = 'Il nome deve essere di almeno 2 caratteri';
                } else if (value.trim().length > 100) {
                    newErrors.teamNome = 'Il nome non può superare i 100 caratteri';
                } else {
                    delete newErrors.teamNome;
                }
                break;

            case 'legaId':
                if (!value) {
                    newErrors.legaId = 'La lega è obbligatoria';
                } else {
                    delete newErrors.legaId;
                }
                break;

            case 'sportId':
                if (!value) {
                    newErrors.sportId = 'Lo sport è obbligatorio';
                } else {
                    delete newErrors.sportId;
                }
                break;

            case 'foto':
                // Nessuna validazione: campo sempre opzionale
                delete newErrors.foto;
                break;

            case 'ordinamento':
                if (value && (isNaN(value) || value < 0)) {
                    newErrors.ordinamento = 'L\'ordinamento deve essere un numero positivo';
                } else {
                    delete newErrors.ordinamento;
                }
                break;

            case 'note':
                if (value && value.length > 500) {
                    newErrors.note = 'Le note non possono superare i 500 caratteri';
                } else {
                    delete newErrors.note;
                }
                break;

            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Gestisce il cambiamento dei campi
    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));

        // Validazione in tempo reale
        validateField(name, value);

        // Gestione preview foto
        if (name === 'foto') {
            if (value && value.trim()) {
                setFotoPreview(value.trim());
                setFotoError(false);
            } else {
                setFotoPreview('');
                setFotoError(false);
            }
        }
    };

    // Gestisce errore caricamento foto
    const handleFotoError = () => {
        setFotoError(true);
        // Non aggiungere errori a errors.foto, la preview non deve bloccare il salvataggio
    };

    // Gestisce successo caricamento foto
    const handleFotoLoad = () => {
        setFotoError(false);
        if (errors.foto && errors.foto.includes('Impossibile caricare')) {
            const newErrors = { ...errors };
            delete newErrors.foto;
            setErrors(newErrors);
        }
    };

    // Valida tutto il form
    const validateForm = () => {
        const fieldsToValidate = ['teamNome', 'legaId', 'sportId', 'foto', 'ordinamento', 'note'];
        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    // Salva la squadra
    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            // Prepara i dati per l'invio
            const dataToSend = {
                nome: formData.teamNome.trim(),
                legaId: parseInt(formData.legaId),
                sportId: parseInt(formData.sportId),
                foto: (formData.foto || "").trim(),
                ordinamento: formData.ordinamento ? parseInt(formData.ordinamento) : null,
                note: formData.note.trim() || null,
                flVisibile: formData.flVisibile,
                flAttivo: formData.flAttivo,
                flAnnullato: false
            };

            console.log("dataToSend : ", dataToSend);


            const response = await axios.post(
                `${host}/tbl-team`,
                dataToSend,
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Squadra "${formData.teamNome}" creata con successo`, { type: 'success' });

            // Naviga alla pagina di dettaglio della nuova squadra
            if (response.data && response.data.id) {
                navigate(`/squadre/${response.data.id}`);
            } else {
                navigate('/squadre');
            }

        } catch (error) {
            console.error("Errore nella creazione squadra:", error);

            if (error.response?.data?.message) {
                notify(`Errore: ${error.response.data.message}`, { type: 'error' });
            } else if (error.response?.status === 409) {
                notify("Una squadra con questo nome esiste già in questa lega", { type: 'error' });
            } else {
                notify("Errore nella creazione della squadra", { type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    // Reset form
    const handleReset = () => {
        setFormData({
            teamNome: '',
            legaId: '',
            sportId: '',
            foto: '',
            ordinamento: '',
            note: '',
            flVisibile: true,
            flAttivo: true
        });
        setErrors({});
        setTouched({});
        setFotoPreview('');
        setFotoError(false);
    };

    // Carica dati al mount
    React.useEffect(() => {
        setLoading(true);
        Promise.all([loadLeghe(), loadSports()]).finally(() => {
            setLoading(false);
        });
    }, []);

    // Loading iniziale
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                        Caricamento dati...
                    </Typography>
                </Box>
            </Box>
        );
    }

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.teamNome.trim() && formData.legaId && formData.sportId && !hasErrors;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >

                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/squadre')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Squadre
                    </Link>
                    <Typography variant="body2" color="text.primary">
                        Nuova Squadra
                    </Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            ⚽ Crea Nuova Squadra
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Inserisci i dettagli per creare una nuova squadra
                        </Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/squadre')}
                    >
                        Indietro
                    </Button>
                </Box>

                {/* Form */}
                <Grid container spacing={3}>

                    {/* Colonna principale - Dati squadra */}
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informazioni Squadra
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>

                                    {/* Nome squadra */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Nome Squadra *"
                                            value={formData.teamNome}
                                            onChange={(e) => handleFieldChange('teamNome', e.target.value)}
                                            error={touched.teamNome && !!errors.teamNome}
                                            helperText={touched.teamNome && errors.teamNome}
                                            placeholder="Inserisci il nome della squadra"
                                        />
                                    </Grid>

                                    {/* Lega */}
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            error={touched.legaId && !!errors.legaId}
                                        >
                                            <InputLabel>Lega *</InputLabel>
                                            <Select
                                                value={formData.legaId}
                                                label="Lega *"
                                                onChange={(e) => handleFieldChange('legaId', e.target.value)}
                                                disabled={loadingLeghe}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <EmojiEventsIcon sx={{ color: '#1565c0' }} />
                                                    </InputAdornment>
                                                }
                                            >
                                                {loadingLeghe ? (
                                                    <MenuItem disabled>
                                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                                        Caricamento...
                                                    </MenuItem>
                                                ) : (
                                                    leghe.map(lega => (
                                                        <MenuItem key={lega.id} value={lega.id}>
                                                            {lega.nome}
                                                            {lega.descrizione && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                    - {lega.descrizione}
                                                                </Typography>
                                                            )}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                            {touched.legaId && errors.legaId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.legaId}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    {/* Sport */}
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            error={touched.sportId && !!errors.sportId}
                                        >
                                            <InputLabel>Sport *</InputLabel>
                                            <Select
                                                value={formData.sportId}
                                                label="Sport *"
                                                onChange={(e) => handleFieldChange('sportId', e.target.value)}
                                                disabled={loadingSports}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <SportsIcon sx={{ color: '#e65100' }} />
                                                    </InputAdornment>
                                                }
                                            >
                                                {loadingSports ? (
                                                    <MenuItem disabled>
                                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                                        Caricamento...
                                                    </MenuItem>
                                                ) : (
                                                    sports.map(sport => (
                                                        <MenuItem key={sport.id} value={sport.id}>
                                                            {sport.nome}
                                                            {sport.descrizione && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                    - {sport.descrizione}
                                                                </Typography>
                                                            )}
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                            {touched.sportId && errors.sportId && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                    {errors.sportId}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>

                                    {/* URL Foto */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="URL Logo/Foto"
                                            value={formData.foto}
                                            onChange={(e) => handleFieldChange('foto', e.target.value)}
                                            error={false}
                                            helperText={fotoError ? "Immagine non caricabile: il link non è pubblico o il servizio non permette l'anteprima diretta. Puoi comunque salvare la squadra." : " "}
                                            placeholder="https://esempio.com/logo.png"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PhotoCameraIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: formData.foto && (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Anteprima">
                                                            <IconButton size="small">
                                                                <PreviewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Ordinamento */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Ordinamento"
                                            type="number"
                                            value={formData.ordinamento}
                                            onChange={(e) => handleFieldChange('ordinamento', e.target.value)}
                                            error={touched.ordinamento && !!errors.ordinamento}
                                            helperText={touched.ordinamento && errors.ordinamento || "Numero per ordinare le squadre"}
                                            placeholder="0"
                                            inputProps={{ min: 0, step: 1 }}
                                        />
                                    </Grid>

                                    {/* Note */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Note"
                                            multiline
                                            rows={3}
                                            value={formData.note}
                                            onChange={(e) => handleFieldChange('note', e.target.value)}
                                            error={touched.note && !!errors.note}
                                            helperText={touched.note && errors.note || `${formData.note.length}/500 caratteri`}
                                            placeholder="Note aggiuntive sulla squadra..."
                                        />
                                    </Grid>

                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Colonna laterale - Preview e controlli */}
                    <Grid item xs={12} md={4}>

                        {/* Preview squadra */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Anteprima Squadra
                                </Typography>

                                <Avatar
                                    src={fotoPreview && !fotoError ? `${host}/image-proxy-mw?url=${encodeURIComponent(fotoPreview)}` : ''}
                                    alt={formData.teamNome}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        margin: '0 auto 16px',
                                        bgcolor: 'primary.main',
                                        fontSize: '2rem'
                                    }}
                                    onError={handleFotoError}
                                    onLoad={handleFotoLoad}
                                >
                                    {!fotoPreview || fotoError ?
                                        (formData.teamNome ? formData.teamNome.charAt(0).toUpperCase() : '⚽') :
                                        null
                                    }
                                </Avatar>

                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {formData.teamNome || 'Nome Squadra'}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                                    {formData.legaId && (
                                        <Typography variant="body2" color="primary">
                                            {leghe.find(l => l.id === parseInt(formData.legaId))?.nome || 'Lega'}
                                        </Typography>
                                    )}
                                    {formData.legaId && formData.sportId && (
                                        <Typography variant="body2" color="text.secondary">•</Typography>
                                    )}
                                    {formData.sportId && (
                                        <Typography variant="body2" color="primary">
                                            {sports.find(s => s.id === parseInt(formData.sportId))?.nome || 'Sport'}
                                        </Typography>
                                    )}
                                </Box>

                                {formData.note && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        {formData.note.length > 100 ? `${formData.note.substring(0, 100)}...` : formData.note}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Impostazioni */}
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                    Impostazioni
                                </Typography>

                                <Box sx={{ space: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flVisibile}
                                                onChange={(e) => handleFieldChange('flVisibile', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Squadra visibile pubblicamente"
                                        sx={{ mb: 1 }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.flAttivo}
                                                onChange={(e) => handleFieldChange('flAttivo', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Squadra attiva"
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Errori */}
                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Correggere i seguenti errori:
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}>
                                            <Typography variant="body2">{error}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Crea Squadra'}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleReset}
                                disabled={saving}
                            >
                                Reset Form
                            </Button>
                        </Box>
                    </Grid>

                </Grid>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        * Campi obbligatori
                    </Typography>
                </Box>

            </motion.div>
        </Box>
    );
};